import React, {useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexColumn, Text} from '@forecast-it/design-system';
import {AudienceTargets, buildDeploymentView, DeploymentSource, isViewerCompanyEnabled} from './novaDeploymentData';
import {NovaDeployAudienceModal} from './NovaDeployAudienceModal';
import {NovaDeployAudienceBar} from './NovaDeployAudienceBar';
import {NovaDeployAudienceTable} from './NovaDeployAudienceTable';
import {DeployDirectory, encodeCompanyGlobalId} from './novaDeployDirectory';
import {NovaStudioColor} from '../shared/novaStudioColors';

interface Props {
	/** The agent's own name — shown as the per-company audience modal's title. */
	agentName: string;
	deployment: DeploymentSource | null;
	directory: DeployDirectory;
	isDirectoryLoading: boolean;
	isSubmitting: boolean;
	/** A deployment read is in flight and there is nothing to show yet. */
	isDeploymentLoading: boolean;
	/** The last deployment read failed. Distinct from an agent that genuinely serves nobody. */
	hasDeploymentReadFailed: boolean;
	/**
	 * The viewer's own company, as the schema's `Viewer.company.backendId` — a raw integer, encoded
	 * differently from an audience entry's `companyId` (`CompanyType:<n>`, base64). Null until the
	 * query that carries it has answered. Re-encoded here, right before it is ever compared against an
	 * audience entry — see `isViewerCompanyEnabled` in novaDeploymentData.ts for why comparing the two
	 * encodings directly always fails, silently.
	 */
	viewerCompanyId: number | null;
	/**
	 * Whether the agent's saved current version has both required prompts. The service refuses to
	 * add anyone to an agent that cannot run, so while this is false the add controls are disabled
	 * and the tab says what to save. Removing is never blocked: that is how such an agent is withdrawn.
	 */
	isServedVersionRunnable: boolean;
	/**
	 * Adds and removes companies/people from the agent's audience in one transaction — see
	 * `AudienceTargets`'s own comment for why both sides travel together rather than as two calls.
	 */
	onAudienceSubmit: (add: AudienceTargets, remove: AudienceTargets) => void;
	/** Called the first time the audience picker opens, so the directory is fetched only when it is needed. */
	onOpenDirectory: () => void;
}

const EMPTY_TARGETS: AudienceTargets = {companyIds: [], persons: []};

/**
 * The audience box. Collapsed it is just the blue bar; expanded, the bar stays as its header and the
 * table below it is white, with one border around the pair so they read as one object rather than
 * two stacked ones.
 *
 * `overflow: hidden` is what makes the header's square corners follow the container's rounded ones —
 * without it the blue bleeds past the radius at the top two corners.
 */
const AudienceBox = styled.div`
	border: 1px solid ${NovaStudioColor.border};
	border-radius: 8px;
	overflow: hidden;
	background: #ffffff;
`;

export const NovaDeployTab = ({
	agentName,
	deployment,
	directory,
	isDirectoryLoading,
	isSubmitting,
	isDeploymentLoading,
	hasDeploymentReadFailed,
	viewerCompanyId,
	isServedVersionRunnable,
	onAudienceSubmit,
	onOpenDirectory,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const view = buildDeploymentView(deployment);
	// Null when the modal is closed. Otherwise the company being edited, or `null` for the `+` control
	// — a brand-new company that is not yet part of the audience.
	const [audienceModalCompanyId, setAudienceModalCompanyId] = useState<string | null | undefined>(undefined);
	const isAudienceModalOpen = audienceModalCompanyId !== undefined;
	// Collapsed by default: the mockup this tab follows shows the tab first appearing as the bar
	// alone, caret pointing down to open it — the table only appears once that caret is clicked.
	const [isExpanded, setIsExpanded] = useState(false);

	const openAudience = (companyId: string | null): void => {
		onOpenDirectory();
		setAudienceModalCompanyId(companyId);
	};

	// Expanding the table is the other place names must resolve at render time — not only opening a
	// modal. `onOpenDirectory` already no-ops once the directory is loaded or in flight, so this costs
	// nothing on repeat expands, and nothing at all on collapse.
	const handleToggleExpanded = (): void => {
		setIsExpanded(current => {
			const next = !current;
			if (next) {
				onOpenDirectory();
			}
			return next;
		});
	};

	const viewerCompanyGlobalId = viewerCompanyId != null ? encodeCompanyGlobalId(viewerCompanyId) : null;
	const isViewerCompanyActive = isViewerCompanyEnabled(view, viewerCompanyGlobalId);

	// Neither a failed read nor one still in flight has anything true to report — the same condition the
	// banner immediately above already gates on. Without this the bar would assert real-looking counts
	// (and offer a toggle that looks actionable) it cannot back up: `viewerCompanyId` in particular goes
	// back to null for the duration of a refetch (`clearDataOnFetch` blanks the query that carries it),
	// so a toggle rendered during that window would silently do nothing on click.
	const hasDeploymentAnswer = !hasDeploymentReadFailed && !(deployment === null && isDeploymentLoading);

	// The one place this glue actually runs: encode the viewer's raw company id the way an audience
	// entry's own id is encoded, then add or remove it through the same mutation path the audience
	// modal uses. Skipped only when the viewer's company id has not arrived yet — there is then
	// nothing correct to submit.
	const handleSetViewerCompanyActive = (active: boolean): void => {
		if (!viewerCompanyGlobalId) {
			return;
		}
		if (active && !isServedVersionRunnable) {
			return;
		}
		const target: AudienceTargets = {companyIds: [viewerCompanyGlobalId], persons: []};
		onAudienceSubmit(active ? target : EMPTY_TARGETS, active ? EMPTY_TARGETS : target);
	};

	const editingRow =
		audienceModalCompanyId != null ? view.rows.find(row => row.companyId === audienceModalCompanyId) ?? null : null;

	return (
		// FlexColumn does not forward data-* attributes, so the tab's own hook goes on a plain
		// wrapper rather than silently rendering nothing.
		<div data-cy="nova-deploy-tab">
			<FlexColumn>
				{/* A failed read and an in-flight one are different answers, and neither may be shown as
				    the other. An audience that serves nobody states itself through the bar's own zeroed
				    counts below, so it needs no line of its own. */}
				{hasDeploymentReadFailed ? (
					<Text data-cy="nova-deploy-read-failed">{formatMessage({id: 'nova.deploy.readFailed'})}</Text>
				) : deployment === null && isDeploymentLoading ? (
					<Text data-cy="nova-deploy-loading">{formatMessage({id: 'nova.deploy.loading'})}</Text>
				) : null}

				{!isServedVersionRunnable ? (
					<Text data-cy="nova-deploy-not-runnable">{formatMessage({id: 'nova.deploy.notRunnable'})}</Text>
				) : null}

				{/* One box, not two stacked ones. The table is nested inside the same guard as the bar
				    rather than standing beside it: a read that failed or is still in flight has no rows
				    to show either, and rendering an empty table under a hidden bar would state that the
				    audience is empty when nothing is known about it yet. */}
				{hasDeploymentAnswer ? (
					<AudienceBox>
						<NovaDeployAudienceBar
							companyCount={view.companyCount}
							userCount={view.userCount}
							isExpanded={isExpanded}
							onToggleExpanded={handleToggleExpanded}
							onAddCompany={() => openAudience(null)}
							canAdd={isServedVersionRunnable}
							isViewerCompanyActive={isViewerCompanyActive}
							onSetViewerCompanyActive={handleSetViewerCompanyActive}
							isSubmitting={isSubmitting}
						/>

						{isExpanded ? (
							<NovaDeployAudienceTable
								rows={view.rows}
								directory={directory}
								onEditCompany={companyId => openAudience(companyId)}
								isSubmitting={isSubmitting}
							/>
						) : null}
					</AudienceBox>
				) : null}

				{isAudienceModalOpen ? (
					<NovaDeployAudienceModal
						agentName={agentName}
						directory={directory}
						editingCompanyId={audienceModalCompanyId ?? null}
						row={editingRow}
						enabledCompanyIds={view.rows.map(row => row.companyId)}
						isLoading={isSubmitting}
						isDirectoryLoading={isDirectoryLoading}
						canAdd={isServedVersionRunnable}
						onSubmit={(add, remove) => {
							onAudienceSubmit(add, remove);
							setAudienceModalCompanyId(undefined);
						}}
						onClose={() => setAudienceModalCompanyId(undefined)}
					/>
				) : null}
			</FlexColumn>
		</div>
	);
};
